/**
 * The parts of a conversation that are not questions.
 *
 * An operator who says "hello" has not asked anything, and answering a greeting
 * with a keyword search is the same failure as answering "what is the condition"
 * with work-order titles — a machine that heard the words and missed the act.
 * Equally, "thanks, that's all" is an instruction to stop, and a conversation
 * loop that does not recognise it keeps the microphone open after the operator
 * has walked away.
 *
 * Kept deliberately literal. These matchers open and close a live microphone,
 * so the cost of a false positive is a session that ends mid-sentence and the
 * cost of a false negative is a microphone left listening. Neither is improved
 * by cleverness.
 */

export function isGreeting(q: string): boolean {
  return (
    /^\s*(hi|hey|hello|yo|good (morning|afternoon|evening|day))\b[\s,.!]*(sky\b)?[\s,.!]*$/i.test(q) ||
    /^\s*(hi|hey|hello)\s+sky\b[\s,.!]*$/i.test(q)
  );
}

/**
 * "Thanks", "that's all", "nothing else" — an instruction to stop listening.
 *
 * Matched on the whole utterance rather than its opening, because people
 * combine these: "thanks, that's all" opens with a word the first version
 * recognised and then failed the end-of-string anchor, so the microphone stayed
 * open on the clearest possible instruction to close it. Every word has to be a
 * sign-off word, and at least one has to be a real one — which keeps "thanks,
 * now show me the projects" a question.
 */
const SIGN_OFF_WORDS = new Set([
  "thanks",
  "thank",
  "you",
  "cheers",
  "ta",
  "that",
  "thats",
  "that's",
  "all",
  "it",
  "is",
  "nothing",
  "else",
  "no",
  "not",
  "now",
  "stop",
  "goodbye",
  "bye",
  "done",
  "finished",
  "enough",
  "ok",
  "okay",
  "right",
  "fine",
  "good",
  "great",
  "sky",
  "for",
]);
const SIGN_OFF_CORE = new Set([
  "thanks",
  "thank",
  "cheers",
  "ta",
  "nothing",
  "stop",
  "goodbye",
  "bye",
  "done",
  "finished",
  "enough",
  "all",
]);

export function isSignOff(q: string): boolean {
  const words = q
    .toLowerCase()
    .replace(/[^a-z'\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0 || words.length > 6) return false;
  if (!words.every((w) => SIGN_OFF_WORDS.has(w))) return false;
  if (words.some((w) => SIGN_OFF_CORE.has(w))) return true;
  /* "That's it" carries no core word on its own; the pair is what means it. */
  return words.includes("it") && words.some((w) => /^that'?s?$/.test(w));
}

/** A short acknowledgement, so a long lookup does not feel like being ignored. */
export function acknowledgement(): string {
  return "Okay, let me search my database.";
}

/**
 * What she offers when greeted.
 *
 * Named departments rather than an open question. "What can I help with" puts
 * the work of knowing what she can do back on the operator; naming three things
 * she is actually good at is the difference between a prompt and a menu.
 */
export function whatWouldYouLike(name: string | null): string[] {
  return [
    name ? `Hello ${name}.` : "Hello.",
    "What would you like me to pull together? I can brief you on operations, on maintenance, or on the projects — what is late, what is over budget, what is stopped. Or ask about the screen you are on.",
  ];
}

/* ── interrupting her ─────────────────────────────────────────────────── */

/**
 * The words that stop her mid-sentence.
 *
 * Small and closed on purpose. This runs against a microphone that is open
 * *while she is talking*, which is the one situation the rest of this module
 * carefully avoids — so it listens for a handful of words and is deaf to
 * everything else. A general recogniser running through her own answer would
 * transcribe the room and hand it to `ask`.
 */
const INTERRUPTIONS = ["stop", "wait", "cancel", "quiet", "enough", "shut up", "hold on", "never mind"];

/**
 * Whether the operator just told her to stop — as against her having said one
 * of those words herself.
 *
 * The second half is the whole difficulty. A laptop speaker and a laptop
 * microphone are inches apart, so she hears her own answer, and an answer
 * about a stopped meter or work that has to wait contains the stop words
 * verbatim. `beingSpoken` is what she is saying at that moment, and anything
 * already in it is treated as an echo.
 *
 * That trade is deliberately one-sided. Ignoring a real interruption costs a
 * sentence the operator has to sit through; acting on an echo means she cuts
 * herself off mid-answer, every time she mentions stopped work — which would
 * make the feature read as broken rather than as imperfect.
 */
export function isInterruption(heard: string, beingSpoken = ""): boolean {
  const said = heard.toLowerCase();
  const mine = beingSpoken.toLowerCase();
  return INTERRUPTIONS.some((w) => said.includes(w) && !mine.includes(w));
}

/** The words the interruption watcher listens for. */
export const interruptionWords: readonly string[] = INTERRUPTIONS;
